import { Box, Flex, Heading, Icon, Text, VStack } from '@chakra-ui/react';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { FC, KeyboardEvent, useState } from 'react';
import { ComplianceSuccessfulCheck } from './types';

interface SuccessfulChecksListProps {
  checks: ComplianceSuccessfulCheck[];
  /**
   * Called with a row's field ids when the row is clicked/activated. The page
   * scrolls/centers the PDF on the first id. Omitted when rows aren't
   * interactive.
   */
  onSelectFields?: (fieldIds: string[]) => void;
}

/**
 * Collapsible card of passing compliance checks for the selected document,
 * shown as green-check rows (e.g. "Seller signatures/initials"). Collapsed by
 * default — the passing checks are reassurance, not action items, so they stay
 * tucked behind the header until expanded. Renders nothing when there are no
 * checks.
 *
 * Each row with field ids is clickable: it calls `onSelectFields(row.fieldIds)`
 * so the consumer can scroll/center the PDF on the field.
 */
export const SuccessfulChecksList: FC<SuccessfulChecksListProps> = ({
  checks,
  onSelectFields,
}) => {
  const [expanded, setExpanded] = useState(false);

  if (checks.length === 0) {
    return null;
  }

  const heading =
    checks.length === 1
      ? '1 successful check'
      : `${checks.length} successful checks`;

  return (
    <Box
      bg="whiteAlpha.50"
      borderColor="whiteAlpha.100"
      borderRadius="10px"
      borderWidth={1}
      p={5}
    >
      <VStack align="stretch" spacing={2}>
        <Flex
          align="center"
          aria-expanded={expanded}
          cursor="pointer"
          justify="space-between"
          role="button"
          tabIndex={0}
          onClick={() => setExpanded((previous) => !previous)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setExpanded((previous) => !previous);
            }
          }}
        >
          <Heading
            color="compliance.status.success"
            fontSize="lg"
            fontWeight="normal"
            size="headline"
          >
            {heading}
          </Heading>
          <Icon
            as={ChevronDownIcon}
            boxSize="18px"
            color="compliance.text.secondary"
            flexShrink={0}
            transform={expanded ? undefined : 'rotate(-90deg)'}
            transition="transform 0.15s ease"
          />
        </Flex>

        {expanded && (
          <VStack align="stretch" spacing={1}>
            {checks.map((check, index) => {
              const fieldIds = check.fieldIds ?? [];
              const isClickable = onSelectFields != null && fieldIds.length > 0;

              const handleSelect = (): void => {
                if (isClickable) {
                  onSelectFields?.(fieldIds);
                }
              };

              const handleKeyDown = (event: KeyboardEvent): void => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleSelect();
                }
              };

              return (
                <Flex
                  key={check.ruleUuid ?? `successful-check-${index}`}
                  _hover={isClickable ? { bg: 'whiteAlpha.100' } : undefined}
                  align="center"
                  borderRadius="6px"
                  cursor={isClickable ? 'pointer' : undefined}
                  gap={1}
                  mx={-2}
                  px={2}
                  py={1}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  onClick={isClickable ? handleSelect : undefined}
                  onKeyDown={isClickable ? handleKeyDown : undefined}
                >
                  <Icon
                    as={CheckIcon}
                    boxSize="14px"
                    color="compliance.status.success"
                    flexShrink={0}
                  />
                  <Text color="compliance.text.primary" fontSize="sm">
                    {check.message}
                  </Text>
                </Flex>
              );
            })}
          </VStack>
        )}
      </VStack>
    </Box>
  );
};
